import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { firebasePhoneAuth } from '../services/firebasePhoneAuth'
import { trackSignUp, trackLogin } from '../services/adTracking'
import { DateInput } from '../components/DateInput'
import { validateDate } from '../utils/inputValidation'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

export default function SignUp() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    phoneOrEmail: '',
    dateOfBirth: ''
  })
  const [step, setStep] = useState<'initial' | 'details' | 'verification'>('initial')
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [contactType, setContactType] = useState<'email' | 'phone'>('email')
  const [otpTimer, setOtpTimer] = useState(0)
  const [otpExpiryTime, setOtpExpiryTime] = useState<number | null>(null)
  const [canResend, setCanResend] = useState(false)
  const [resendAttempts, setResendAttempts] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  // Get return URL from navigation state
  const from = (location.state as any)?.from || '/'

  // Initialize Firebase reCAPTCHA on mount
  useEffect(() => {
    const initFirebase = async () => {
      const result = await firebasePhoneAuth.initRecaptcha('recaptcha-container-signup', false)
      if (!result.success) {
        console.error('reCAPTCHA initialization failed:', result.message)
      }
    }
    initFirebase()

    // Cleanup on unmount
    return () => {
      firebasePhoneAuth.cleanupRecaptcha()
    }
  }, [])

  // OTP countdown timer
  useEffect(() => {
    if (otpTimer > 0) {
      const interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [otpTimer])

  // Check OTP expiry
  useEffect(() => {
    if (otpExpiryTime && step === 'verification') {
      const checkExpiry = setInterval(() => {
        const now = Date.now()
        if (now >= otpExpiryTime) {
          setError('OTP has expired. Please request a new one.')
          setCanResend(true)
          setOtpTimer(0)
        }
      }, 1000)

      return () => clearInterval(checkExpiry)
    }
  }, [otpExpiryTime, step])

  // Format timer display
  const formatTimer = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setLoading(true)
      setError('')

      // Decode JWT to get user info
      const token = credentialResponse.credential
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))

      const userInfo = JSON.parse(jsonPayload)

      console.log('🔐 Signing up with Google...', userInfo.email)
      const result = await api.loginWithGoogle(token, userInfo)

      if (result.success && result.token && result.user) {
        console.log('✅ Sign up successful')
        trackSignUp('google')

        // Update auth context
        login(result.token, result.user)

        // ✅ STANDARDIZED: Trust backend's profileCompleted flag as the single source of truth
        // Backend sets this flag when all required fields are present
        const user = result.user
        const isProfileComplete = user?.profileCompleted === true

        console.log('📊 Profile check:', {
          username: user?.username,
          name: user?.name,
          profileCompleted: user?.profileCompleted,
          isComplete: isProfileComplete
        })

        if (isProfileComplete) {
          // Profile is complete - redirect to return URL or home
          console.log('✅ Profile complete, redirecting to:', from)
          navigate(from, { replace: true })
        } else {
          // Profile incomplete - go to profile setup (will redirect to return URL after setup)
          console.log('⚠️ Profile incomplete, redirecting to profile setup')
          navigate('/profile-setup', { state: { from } })
        }
      } else {
        setError(result.message || 'Sign up failed')
      }
    } catch (err) {
      console.error('Sign up error:', err)
      setError('An error occurred during sign up')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleError = () => {
    setError('Google Sign-Up was cancelled or failed')
    setLoading(false)
  }

  const handleAppleSignUp = async () => {
    // TODO: Implement Apple OAuth
    console.log('Apple sign up')
  }

  const handleInitialContinue = async () => {
    if (!formData.phoneOrEmail) {
      setError('Please enter your email or phone number')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Check if it's email or phone
      const isEmail = formData.phoneOrEmail.includes('@')
      const isPhone = /^\+?\d{10,15}$/.test(formData.phoneOrEmail.replace(/\s/g, ''))

      if (!isEmail && !isPhone) {
        setError('Please enter a valid email or phone number (with country code)')
        setLoading(false)
        return
      }

      if (isPhone) {
        // Use Firebase Phone Auth
        setContactType('phone')

        // Format phone number to E.164
        const formattedPhone = firebasePhoneAuth.formatPhoneNumber(formData.phoneOrEmail)
        console.log('📱 Sending OTP to phone:', formattedPhone)

        const result = await firebasePhoneAuth.sendOTP(formattedPhone)

        if (result.success) {
          console.log('✅ OTP sent successfully to phone')
          setFormData({ ...formData, phoneOrEmail: formattedPhone }) // Update with formatted phone

          // Set timer and expiry
          setOtpTimer(60)
          setOtpExpiryTime(Date.now() + 5 * 60 * 1000)
          setCanResend(false)
          setResendAttempts(prev => prev + 1)

          setStep('verification')
        } else {
          setError(result.message || 'Failed to send verification code to phone')
        }
      } else {
        // Use Email OTP (Backend V2)
        setContactType('email')

        const result = await api.sendOTP(
          formData.phoneOrEmail,
          undefined,
          'signup'
        )

        if (result.success) {
          console.log('✅ OTP sent successfully to email')

          // Set timer and expiry
          setOtpTimer(60)
          setOtpExpiryTime(Date.now() + 5 * 60 * 1000)
          setCanResend(false)
          setResendAttempts(prev => prev + 1)

          setStep('verification')
        } else {
          setError(result.message || 'Failed to send verification code to email')
        }
      }
    } catch (err) {
      console.error('OTP send error:', err)
      setError('Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length < 4) {
      setError('Please enter the verification code')
      return
    }

    setLoading(true)
    setError('')

    try {
      console.log('🔐 Verifying OTP for:', formData.phoneOrEmail, 'Type:', contactType)

      let result: any

      if (contactType === 'phone') {
        // Verify Firebase Phone OTP
        const firebaseResult = await firebasePhoneAuth.verifyOTP(verificationCode)

        if (firebaseResult.success) {
          console.log('✅ Phone verified successfully')
          // For signup, we just need to verify the phone - proceed to details step
          setStep('details')
          return
        } else {
          setError(firebaseResult.message || 'Invalid verification code')
          setLoading(false)
          return
        }
      } else {
        // Verify Email OTP via Backend V2
        result = await api.verifyOTP(
          formData.phoneOrEmail,
          verificationCode,
          ''
        )

        if (result.success) {
          console.log('✅ Email verified successfully')
          setStep('details')
        } else {
          setError(result.message || 'Invalid verification code')
        }
      }
    } catch (err) {
      console.error('Verification error:', err)
      setError('Verification failed')
    } finally {
      setLoading(false)
    }
  }

  const handleCompleteSignUp = async () => {
    if (!formData.name || !formData.username || !formData.dateOfBirth) {
      setError('Please fill in all required fields')
      return
    }

    // Validate date format and age
    const dateValidation = validateDate(formData.dateOfBirth, 13)
    if (!dateValidation.isValid) {
      setError(dateValidation.error || 'Invalid date of birth')
      return
    }

    setLoading(true)
    setError('')

    try {
      const isEmail = formData.phoneOrEmail.includes('@')

      // Convert DD/MM/YYYY to YYYY-MM-DD for backend
      const [day, month, year] = formData.dateOfBirth.split('/')
      const formattedDate = `${year}-${month}-${day}`

      const result = await api.signUp({
        name: formData.name,
        username: formData.username,
        email: isEmail ? formData.phoneOrEmail : formData.email,
        phone: isEmail ? undefined : formData.phoneOrEmail,
        dateOfBirth: formattedDate,
        verificationCode
      })

      if (result.success) {
        console.log('✅ Account created successfully')
        trackSignUp(isEmail ? 'email' : 'phone')
        navigate('/profile-setup')
      } else {
        setError(result.message || 'Sign up failed')
      }
    } catch (err) {
      console.error('Sign up error:', err)
      setError('Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:flex-1 bg-onrecord-orange items-center justify-center">
          <div className="text-white">
            <img
              src="/logo.svg"
              alt="on record"
              className="w-64 h-64 mx-auto mb-8"
            />
            <h1 className="text-5xl font-bold text-center">on record</h1>
            <p className="text-center text-xl mt-4">Where authentic stories come to life</p>
          </div>
        </div>

        {/* Right side - Sign Up Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <img
                src="/logo.svg"
                alt="on record"
                className="w-12 h-12 mb-4 lg:hidden"
              />
              <h1 className="text-5xl font-bold mb-2">Join OnRecord</h1>
              <h2 className="text-2xl text-gray-600 mb-8">Create your account</h2>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            {step === 'initial' && (
              <>
                {/* Social Sign Up Buttons */}
                <div className="space-y-3 mb-4">
                  <button
                    onClick={() => {
                      const googleButton = document.querySelector('[aria-labelledby="button-label"]') as HTMLElement;
                      if (googleButton) googleButton.click();
                    }}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign in with Google
                  </button>

                  {/* Hidden Google Login */}
                  <div className="hidden">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      size="large"
                      text="signup_with"
                      disabled={loading}
                    />
                  </div>

                  <button
                    onClick={handleAppleSignUp}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Sign in with Apple
                  </button>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-gray-500">or</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Phone/Email Input */}
                <div className="space-y-4">
                  <input
                    type="text"
                    value={formData.phoneOrEmail}
                    onChange={(e) => setFormData({ ...formData, phoneOrEmail: e.target.value })}
                    placeholder="email@example.com or +919876543210"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
                    disabled={loading}
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    📧 Email or 📱 Phone (+91 for India)
                  </p>

                  {/* reCAPTCHA container - required for Firebase Phone Auth */}
                  <div id="recaptcha-container-signup"></div>

                  <button
                    onClick={handleInitialContinue}
                    className="w-full bg-onrecord-orange text-white font-bold py-3 rounded-full hover:bg-orange-600 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Sending code...' : 'Continue'}
                  </button>
                </div>

                <div className="mt-8 text-gray-500 text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="text-onrecord-orange hover:underline">
                    Sign in
                  </Link>
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  By signing up, you agree to our{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-onrecord-orange hover:underline"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-onrecord-orange hover:underline"
                  >
                    Privacy Policy
                  </a>
                </div>

                {/* Footer Links */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs text-gray-500">
                    <a href="/about" className="hover:underline">About</a>
                    <a href="/terms" className="hover:underline">Terms</a>
                    <a href="/privacy" className="hover:underline">Privacy</a>
                    <a href="/cookies" className="hover:underline">Cookies</a>
                  </div>
                  <div className="mt-2 text-center text-xs text-gray-500">
                    © {new Date().getFullYear()} on record
                  </div>
                </div>
              </>
            )}

            {step === 'verification' && (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    We sent a code to <strong>{formData.phoneOrEmail}</strong>
                  </p>
                </div>

                <div className="space-y-4">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter verification code"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent text-center text-2xl tracking-widest"
                    maxLength={6}
                    disabled={loading}
                  />

                  <button
                    onClick={handleVerifyCode}
                    className="w-full bg-onrecord-orange text-white font-bold py-3 rounded-full hover:bg-orange-600 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>

                  <button
                    onClick={() => setStep('initial')}
                    className="w-full text-gray-600 hover:text-gray-800"
                  >
                    Change phone/email
                  </button>
                </div>
              </>
            )}

            {step === 'details' && (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Create your profile
                  </p>
                </div>

                <div className="space-y-4">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
                    disabled={loading}
                  />

                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    placeholder="Username"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
                    disabled={loading}
                  />

                  {!formData.phoneOrEmail.includes('@') && (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email address (optional)"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
                      disabled={loading}
                    />
                  )}

                  <DateInput
                    label="Date of Birth"
                    value={formData.dateOfBirth}
                    onChange={(value) => setFormData({ ...formData, dateOfBirth: value })}
                    required
                    minAge={13}
                    disabled={loading}
                    helperText="This won't be public. You must be 13 years old to use OnRecord."
                  />

                  <button
                    onClick={handleCompleteSignUp}
                    className="w-full bg-onrecord-orange text-white font-bold py-3 rounded-full hover:bg-orange-600 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Create account'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  )
}
